import type { Compactor } from "react-grid-layout";
import {
  horizontalCompactor,
  noCompactor,
  verticalCompactor,
} from "react-grid-layout";
import type {
  DragConfig,
  DropConfig,
  GridConfig,
  LayoutConstraint,
  PositionStrategy,
  ResizeConfig,
} from "react-grid-layout/core";
import {
  absoluteStrategy,
  aspectRatio as aspectRatioConstraint,
  boundedX as boundedXConstraint,
  boundedY as boundedYConstraint,
  containerBounds as containerBoundsConstraint,
  createScaledStrategy,
  gridBounds as gridBoundsConstraint,
  horizontalOverlapCompactor,
  maxSize,
  minMaxSize as minMaxSizeConstraint,
  minSize,
  noOverlapCompactor,
  snapToGrid,
  transformStrategy,
  verticalOverlapCompactor,
} from "react-grid-layout/core";

import type { ReactGridLayoutSettings } from "./reactGridLayoutSettings.types";

export interface AppliedReactGridLayoutProps {
  gridConfig: Partial<GridConfig>;
  dragConfig: Partial<DragConfig>;
  resizeConfig: Partial<ResizeConfig>;
  dropConfig: Partial<DropConfig>;
  positionStrategy: PositionStrategy;
  compactor: Compactor;
  /**
   * A freshly built array on every settings change, typed mutable because
   * ReactGridLayout's `constraints` prop is mutable. Declaring it readonly and
   * casting at the call site would only hide that mismatch; nothing outside
   * this array's owning `AppliedReactGridLayoutProps` holds a reference to it.
   */
  constraints: LayoutConstraint[];
  autoSize: boolean;
}

function resolvePositionStrategy(
  strategy: ReactGridLayoutSettings["positionStrategy"],
  scale: number,
): PositionStrategy {
  switch (strategy) {
    case "transform":
      return transformStrategy;
    case "absolute":
      return absoluteStrategy;
    case "scaled":
      return createScaledStrategy(scale);
  }
}

function resolveCompactor(
  mode: ReactGridLayoutSettings["compactor"],
): Compactor {
  switch (mode) {
    case "vertical":
      return verticalCompactor;
    case "horizontal":
      return horizontalCompactor;
    case "none":
      return noCompactor;
    case "vertical-overlap":
      return verticalOverlapCompactor;
    case "horizontal-overlap":
      return horizontalOverlapCompactor;
    case "none-overlap":
      return noOverlapCompactor;
  }
}

function buildConstraints(s: ReactGridLayoutSettings): LayoutConstraint[] {
  const constraints: LayoutConstraint[] = [];

  if (s.snap.enabled) {
    constraints.push(snapToGrid(s.snap.x, s.snap.y));
  }
  if (s.aspect.enabled) {
    constraints.push(aspectRatioConstraint(s.aspect.ratio));
  }
  if (s.gridBounds) {
    constraints.push(gridBoundsConstraint);
  }
  if (s.containerBounds) {
    constraints.push(containerBoundsConstraint);
  }
  if (s.boundedX) {
    constraints.push(boundedXConstraint);
  }
  if (s.boundedY) {
    constraints.push(boundedYConstraint);
  }
  if (s.minMaxSize) {
    constraints.push(minMaxSizeConstraint);
  }
  if (s.globalMin.enabled) {
    constraints.push(minSize(s.globalMin.w, s.globalMin.h));
  }
  if (s.globalMax.enabled) {
    constraints.push(maxSize(s.globalMax.w, s.globalMax.h));
  }

  return constraints;
}

export function buildReactGridLayoutProps(
  settings: ReactGridLayoutSettings,
): AppliedReactGridLayoutProps {
  const gridConfig: Partial<GridConfig> = {
    cols: settings.cols,
    rowHeight: settings.rowHeight,
    margin: [settings.marginX, settings.marginY],
    containerPadding:
      settings.containerPaddingMode === "custom"
        ? [settings.containerPaddingX, settings.containerPaddingY]
        : [settings.marginX, settings.marginY],
    maxRows: settings.maxRows.limited
      ? settings.maxRows.value
      : Number.POSITIVE_INFINITY,
  };

  const dragConfig: Partial<DragConfig> = {
    bounded: settings.bounded,
    threshold: settings.dragThreshold,
  };

  const resizeConfig: Partial<ResizeConfig> = {
    handles: [...settings.resizeHandles],
  };

  const dropConfig: Partial<DropConfig> = {
    enabled: false,
    defaultItem: { w: settings.externalDrop.w, h: settings.externalDrop.h },
  };

  return {
    gridConfig,
    dragConfig,
    resizeConfig,
    dropConfig,
    positionStrategy: resolvePositionStrategy(
      settings.positionStrategy,
      settings.positionScale,
    ),
    compactor: resolveCompactor(settings.compactor),
    constraints: buildConstraints(settings),
    autoSize: settings.autoSize,
  };
}
